import { useState, useEffect, useCallback } from "react";
import { Globe, Loader2, CheckCircle, Sparkles, AlertTriangle, MessageSquare, ArrowRight, Download, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { BACKEND_URL, STAGE_IDS, normaliseItem, uid } from "./market/constants";
import ValueChainTab from "./market/ValueChainTab";
import CompetitorLandscapeTab from "./market/CompetitorLandscapeTab";
import { useStrategyModule } from "@/hooks/useStrategyModule";

// Single consolidated view — no tab bar. Only news/trends + competitor landscape.
const VALID_TABS = new Set(["markets"]);

export default function MarketStrategy() {
  const shell = useStrategyModule({ endpointKey: "market", defaultTab: "markets", validTabs: VALID_TABS });
  const { user, companyProfile, loading, saving, activeTab, setActiveTab, urlTab, followups, setFollowups, fetchProgress, postProgress } = shell;

  const [canvasData, setCanvasData] = useState({});
  const [ecosystemQuestions, setEcosystemQuestions] = useState([]);
  const [competitorLandscape, setCompetitorLandscape] = useState({ competitors: [], differentiation_claim: "" });
  const [stressTest, setStressTest] = useState(null);
  const [reviewCard, setReviewCard] = useState(null);
  const [reviewCardOpen, setReviewCardOpen] = useState(false);
  const [generatingCard, setGeneratingCard] = useState(false);

  useEffect(() => { if (user) loadProgress(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProgress = async () => {
    const d = await fetchProgress();
    if (!d) return;
    const raw = d.canvasData || {};
    const filtered = {};
    for (const sid of STAGE_IDS) {
      if (raw[sid]) {
        const s = raw[sid];
        filtered[sid] = {
          create_value: s.create_value || "",
          assumptions: s.assumptions || s.assuming || "",
          items: (s.items || []).map(normaliseItem).filter(Boolean),
        };
      }
    }
    setCanvasData(filtered);
    setFollowups((d.followups || []).map((f) => ({ id: f.id || uid(), text: f.text || "", done: !!f.done, sub_module: f.sub_module || null })));

    // Migrate: if ecosystemQuestions is empty but expertQuestions has content, seed it.
    let eqs = (d.ecosystemQuestions || []).map((q) => ({
      id: q.id || uid(),
      question: q.question || "",
      stakeholder_category: q.stakeholder_category || "",
      person_name: q.person_name || "",
    }));
    if (eqs.length === 0 && Array.isArray(d.expertQuestions) && d.expertQuestions.length > 0) {
      eqs = d.expertQuestions.map((q) => ({
        id: q.id || uid(),
        question: q.text || q.question || "",
        stakeholder_category: "",
        person_name: "",
      }));
    }
    setEcosystemQuestions(eqs);

    const cl = d.competitorLandscape || {};
    setCompetitorLandscape({
      competitors: (cl.competitors || []).map((c) => ({ ...c, id: c.id || uid() })),
      differentiation_claim: cl.differentiation_claim || "",
    });

    if (d.stressTest) setStressTest(d.stressTest);
    if (d.reviewCard) setReviewCard(d.reviewCard);
    // Legacy activeView values may be present but we now only support one view.
    if (!urlTab) setActiveTab("markets");
  };

  const persist = useCallback(async (overrides = {}, showToast = false) => {
    const payload = {
      canvasData, followups, ecosystemQuestions, competitorLandscape,
      stressTest, reviewCard,
      activeView: "markets", ...overrides,
    };
    await postProgress(payload, showToast);
  }, [canvasData, followups, ecosystemQuestions, competitorLandscape, stressTest, reviewCard, postProgress]);

  const generateReviewCard = async () => {
    setGeneratingCard(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/generate-review-card`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ canvasData, stressTest }),
      });
      if (res.ok) {
        const card = await res.json();
        setReviewCard(card);
        setReviewCardOpen(true);
        persist({ reviewCard: card });
        toast.success("Market & Value Chain card ready");
      } else toast.error("Failed to generate card");
    } catch (e) { toast.error("Failed to generate card"); }
    setGeneratingCard(false);
  };

  // "Add as competitor" from the Value Chain news drill-down.
  // Returns:  {status:"added"} | {status:"duplicate", existing}  | {status:"error"}
  const addNewsAsCompetitor = (article) => {
    if (!article || !article.subject_company) return { status: "error" };
    const name = article.subject_company.trim();
    if (!name) return { status: "error" };
    // Case-insensitive match — exact OR substring only when the shorter string is at
    // least 4 chars long (avoids "Bio" matching "NeuroBio", "AAV" matching "NovoAAV" etc).
    const nLower = name.toLowerCase();
    const MIN = 4;
    const existing = (competitorLandscape.competitors || []).find((c) => {
      const cc = (c.company || "").toLowerCase().trim();
      if (!cc) return false;
      if (cc === nLower) return true;
      if (nLower.length >= MIN && cc.includes(nLower)) return true;
      if (cc.length >= MIN && nLower.includes(cc)) return true;
      return false;
    });
    if (existing) return { status: "duplicate", existing };
    const newComp = {
      id: uid(), zone: "direct", horizon: "now",
      company: name, target: "", modality: "", stage: "",
      why_matters: (article.summary || "").slice(0, 300),
      why_win: "", why_fail: "", threat_level: "medium",
      _source_url: article.url,
    };
    const next = { ...competitorLandscape, competitors: [...(competitorLandscape.competitors || []), newComp] };
    setCompetitorLandscape(next);
    persist({ competitorLandscape: next });
    return { status: "added", competitor: newComp };
  };

  if (loading) {
    return (
      <AppLayout user={user}>
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout user={user}>
      <div className="max-w-[1400px]" data-testid="market-strategy-page">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><Globe className="w-5 h-5 text-teal-600" /></div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Market & Competitors</h1>
                <p className="text-sm text-slate-500">Higher-level themes from the last 12 months of pharma news, and your competitive landscape.</p>
              </div>
            </div>
            <Button data-testid="save-progress-btn" variant="outline" size="sm" onClick={() => persist({}, true)}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        </div>

        {/* Consolidated view — news/trends first, then competitor landscape. No tabs. */}
        <div data-testid="tab-panel-markets" className="space-y-8">
          <ValueChainTab
            mode="markets"
            user={user}
            companyProfile={companyProfile}
            canvasData={canvasData}
            setCanvasData={setCanvasData}
            stressTest={stressTest}
            setStressTest={setStressTest}
            persist={persist}
            competitors={competitorLandscape.competitors || []}
            onAddNewsAsCompetitor={addNewsAsCompetitor}
            goToCompetitorTab={() => {
              // Scroll to competitor landscape section since it's now on the same page.
              const el = document.getElementById("competitor-landscape-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />

          <div id="competitor-landscape-section">
            <CompetitorLandscapeTab
              landscape={competitorLandscape}
              setLandscape={setCompetitorLandscape}
              persist={persist}
            />
          </div>
        </div>
      </div>

      {/* Expert Review Card modal (generated from To-do tab) */}
      <ReviewCardDialog
        open={reviewCardOpen}
        onClose={() => setReviewCardOpen(false)}
        reviewCard={reviewCard}
        companyProfile={companyProfile}
        onRegenerate={generateReviewCard}
        generating={generatingCard}
      />
    </AppLayout>
  );
}

function ReviewCardDialog({ open, onClose, reviewCard, companyProfile, onRegenerate, generating }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="review-card-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Market & Value Chain card</DialogTitle>
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-2" /> Print / PDF</Button>
          </div>
          <DialogDescription className="sr-only">Generated Market & Value Chain card for the current company.</DialogDescription>
        </DialogHeader>
        {!reviewCard ? (
          <div className="py-8 text-center text-slate-500">No card yet.</div>
        ) : (
          <div className="space-y-6" data-testid="review-card">
            <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
              <h3 className="text-lg font-bold text-teal-900">{companyProfile?.company_name}</h3>
              <p className="text-teal-700 mt-2">{reviewCard.executive_summary}</p>
            </div>
            {typeof reviewCard.overall_readiness_score === "number" && (
              <div className="bg-slate-50 rounded-lg p-4 border flex items-center justify-between">
                <h4 className="text-sm font-medium">Strategic Readiness</h4>
                <Badge className={reviewCard.overall_readiness_score >= 70 ? "bg-green-100 text-green-700" : reviewCard.overall_readiness_score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>{reviewCard.overall_readiness_score}%</Badge>
              </div>
            )}
            {reviewCard.key_strengths?.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-700 mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Key Strengths</h4>
                <ul className="space-y-2">{reviewCard.key_strengths.map((s, i) => <li key={i} className="text-sm text-slate-600 flex items-start gap-2"><span className="text-green-500">✓</span>{s}</li>)}</ul>
              </div>
            )}
            {reviewCard.strategic_gaps?.length > 0 && (
              <div className="border rounded-lg p-4 border-amber-200 bg-amber-50">
                <h4 className="text-sm font-medium text-amber-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Strategic Gaps</h4>
                <div className="space-y-3">{reviewCard.strategic_gaps.map((gap, i) => (
                  <div key={i} className="text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={gap.priority === "high" ? "border-red-300 text-red-700" : "border-amber-300 text-amber-700"}>{gap.priority}</Badge>
                      <span className="font-medium text-slate-900">{gap.gap}</span>
                    </div>
                    <p className="text-slate-600 pl-4">{gap.recommendation}</p>
                  </div>
                ))}</div>
              </div>
            )}
            {reviewCard.competitive_assessment && (
              <div className="border rounded-lg p-4"><h4 className="text-sm font-medium text-slate-700 mb-2">Competitive Positioning</h4><p className="text-sm text-slate-600">{reviewCard.competitive_assessment}</p></div>
            )}
            {reviewCard.stakeholder_priorities?.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="text-sm font-medium text-purple-700 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Stakeholder Priorities</h4>
                <div className="space-y-2">{reviewCard.stakeholder_priorities.map((s, i) => (
                  <div key={i} className="text-sm"><span className="font-medium text-slate-900">{s.category}</span><p className="text-slate-600">{s.rationale}</p>{s.first_action && <p className="text-xs text-teal-600 mt-0.5">Next: {s.first_action}</p>}</div>
                ))}</div>
              </div>
            )}
            {reviewCard.expert_questions?.length > 0 && (
              <div className="border rounded-lg p-4 bg-violet-50 border-violet-200">
                <h4 className="text-sm font-medium text-violet-900 mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Questions for Expert Session</h4>
                <ol className="space-y-2">{reviewCard.expert_questions.map((q, i) => <li key={i} className="text-sm text-violet-800 flex items-start gap-2"><span className="font-medium">{i + 1}.</span>{q}</li>)}</ol>
              </div>
            )}
            {reviewCard.action_items?.length > 0 && (
              <div className="border rounded-lg p-4 bg-teal-50 border-teal-200">
                <h4 className="text-sm font-medium text-teal-900 mb-3 flex items-center gap-2"><ArrowRight className="w-4 h-4" /> 90-Day Action Items</h4>
                <div className="space-y-3">{reviewCard.action_items.map((item, i) => (
                  <div key={i} className="text-sm bg-white rounded p-2 border border-teal-100">
                    <div className="flex items-center justify-between mb-1"><span className="font-medium text-teal-900">{item.action}</span><Badge variant="outline" className="text-xs">{item.timeline}</Badge></div>
                    <p className="text-slate-600 text-xs">{item.outcome}</p>
                  </div>
                ))}</div>
              </div>
            )}
            <div className="flex justify-end pt-2 border-t">
              <Button onClick={onRegenerate} disabled={generating} data-testid="review-card-regenerate-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Regenerate
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
